/**
 * InsightsPanel — AIM-generated insights list on the dashboard.
 *
 * #221 Wave 1: converted from inert Tailwind utilities to the ferro-*
 * design system (.ferro-dashboard-widget chrome, styled badge pills,
 * skeletons, scrollable list). Two error-state fixes ride along per
 * the approved stories: "Refresh Insights" failures are no longer
 * silent, and no raw machine code (e.g. 'ai_error') ever renders —
 * both load and generate failures map to friendly copy + retry.
 */
import { useState, useEffect, useCallback } from 'react';
import type { ClientConfig } from '@ferro/config';
import { adminAnalyticsApi, type AimInsight } from '../../lib/api.js';

const TYPE_BADGES: Record<string, { label: string; modifier: string }> = {
  revenue: { label: 'Revenue', modifier: 'ferro-insight__badge--revenue' },
  bookings: { label: 'Bookings', modifier: 'ferro-insight__badge--bookings' },
  compliance: { label: 'Compliance', modifier: 'ferro-insight__badge--compliance' },
  services: { label: 'Services', modifier: 'ferro-insight__badge--services' },
};

function formatRelativeTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

type InsightsPanelProps = {
  config: ClientConfig;
};

export function InsightsPanel({ config }: InsightsPanelProps): JSX.Element {
  const apiUrl = config.deployment.apiUrl;
  const [insights, setInsights] = useState<AimInsight[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Which operation produced the visible error — Retry must re-invoke
  // the op that actually failed (PR #245 review): retrying a generate
  // failure with a plain re-fetch would "succeed" into the same stale
  // list the owner just asked to regenerate.
  const [failedOp, setFailedOp] = useState<'load' | 'generate'>('load');

  const fetchInsights = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    const result = await adminAnalyticsApi.getInsights(apiUrl);
    setIsLoading(false);
    if (result.error) {
      // Friendly copy only — never surface raw machine codes (#221 QA).
      // Breadcrumb keeps the raw cause findable in the console — #711
      // sanitizes `error`, so the machine token now lives on `code`.
      console.error(
        '[InsightsPanel] insights load failed:',
        result.code ?? result.error,
      );
      setFailedOp('load');
      setError("Couldn't load insights right now.");
    } else if (result.data) {
      setInsights(result.data.insights || []);
    }
  }, [apiUrl]);

  useEffect(() => {
    void fetchInsights();
  }, [fetchInsights]);

  const handleGenerate = useCallback(async () => {
    setIsGenerating(true);
    setError(null);
    const result = await adminAnalyticsApi.generateInsights(apiUrl);
    setIsGenerating(false);
    if (result.error || !result.data) {
      // Previously this dropped the error on the floor — the button just
      // stopped spinning. Per story G1-S7, tell the owner it failed. The
      // breadcrumb keeps the raw cause — #711 sanitizes `error`, so the
      // machine token now lives on `code`.
      console.error(
        '[InsightsPanel] insights generate failed:',
        result.code ?? result.error ?? 'empty response',
      );
      setFailedOp('generate');
      setError("Couldn't generate insights right now — try again.");
      return;
    }
    void fetchInsights();
  }, [apiUrl, fetchInsights]);

  const handleRetry = failedOp === 'generate' ? handleGenerate : fetchInsights;

  return (
    <article
      className="ferro-dashboard-widget"
      aria-label="Insights"
      data-testid="insights-widget"
    >
      <header className="ferro-dashboard-widget__header">
        <h2 className="ferro-section__subtitle">Insights</h2>
        <button
          type="button"
          onClick={() => void handleGenerate()}
          disabled={isGenerating}
          className="ferro-dashboard-widget__action"
        >
          {isGenerating ? 'Generating…' : 'Refresh Insights'}
        </button>
      </header>

      {error && (
        <div
          className="ferro-dashboard-widget__error ferro-dashboard-widget__error--retry"
          role="alert"
        >
          <span>{error}</span>
          <button
            type="button"
            onClick={() => void handleRetry()}
            className="ferro-dashboard-widget__action"
          >
            Retry
          </button>
        </div>
      )}

      {isLoading ? (
        <div className="ferro-skeleton-stack" data-testid="insights-skeleton">
          {[1, 2, 3].map((i) => (
            <div key={i} className="ferro-skeleton ferro-skeleton--rect" />
          ))}
        </div>
      ) : insights.length === 0 ? (
        <div className="ferro-insight-empty">
          <p className="ferro-insight-empty__text">No insights yet</p>
          <button
            type="button"
            onClick={() => void handleGenerate()}
            disabled={isGenerating}
            className="ferro-btn ferro-btn--primary ferro-btn--sm"
          >
            Generate Insights
          </button>
        </div>
      ) : (
        <div className="ferro-insight-list">
          {insights.map((insight) => {
            const badge = TYPE_BADGES[insight.type] ?? {
              label: insight.type,
              modifier: 'ferro-insight__badge--neutral',
            };
            return (
              <div key={insight.id} className="ferro-insight">
                <div className="ferro-insight__top">
                  <div className="ferro-insight__heading">
                    <span className={`ferro-insight__badge ${badge.modifier}`}>
                      {badge.label}
                    </span>
                    <h3 className="ferro-insight__title">{insight.title}</h3>
                  </div>
                  <span className="ferro-insight__time">
                    {formatRelativeTime(insight.generatedAt)}
                  </span>
                </div>
                <p className="ferro-insight__summary">{insight.summary}</p>
              </div>
            );
          })}
        </div>
      )}
    </article>
  );
}
